import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { useParams, useHistory } from "react-router";
import Select from "react-select";
import PageHeading from "components/PageHeading";
import OneTimeUseCoupon from "components/coupon/OneTimeUseCoupon";
import { Actions as ACTIONS, DISCOUNT_TYPE } from "store/coupon/action";
import { Actions as LOCATIONACTIONS } from "store/location/action";
import { Actions as NOTIFICATIONACTIONS } from "store/notifications/action";
import { daysOfWeek } from "components/util.js";
import DatePicker from "react-datepicker";
import cleanDeep from "clean-deep";
import { format, parse } from "date-fns";
import CouponBookings from "components/coupon/CouponBookings";
import CouponCustomers from "components/coupon/CouponCustomers";
import Tab from "../Tab";
import "react-datepicker/dist/react-datepicker.css";
import "styles/datepicker.css";

const Coupon = ({
  coupon,
  locations,
  listLocations,
  saveCoupon,
  getCoupon,
  clearCoupon,
  addNotification,
  deleteCoupon,
}) => {
  let history = useHistory();
  let { id } = useParams();
  const [initialId] = useState(coupon?.id);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, watch, setValue, reset } = useForm();

  const availableDays = watch("availableDays");
  const expireDate = watch("expireDate");
  const validFrom = watch("validFrom");
  const discountType = watch("discountType");

  const availableDaysWithBlank = (availableDays && [...availableDays, {}]) || [
    {},
  ];

  register({ name: "availableDays", type: "custom" }, { required: false });
  register({ name: "expireDate", type: "custom" }, { required: false });
  register({ name: "validFrom", type: "custom" }, { required: false });

  useEffect(() => {
    if (!locations) listLocations(1000, null);
  }, []);

  useEffect(() => {
    if (id !== "new" && (!coupon?.id || coupon?.id !== id)) {
      getCoupon(id);
    } else if (id == "new") {
      clearCoupon();
    } else {
      const couponData = { ...coupon };

      const availableDays =
        couponData.availableDays &&
        couponData.availableDays
          .filter((row) => row.day && row.opensAt && row.closesAt)
          .map((row) => ({
            day: row.day,
            opensAt: parse(row.opensAt, "HH:mm", new Date()),
            closesAt: parse(row.closesAt, "HH:mm", new Date()),
          }));

      couponData.availableDays = availableDays;

      if (couponData.expireDate) {
        const expireDate = new Date(couponData.expireDate);
        if (!isNaN(expireDate.getTime()))
          couponData.expireDate = new Date(couponData.expireDate);
      }

      if (couponData.validFrom) {
        const validFrom = new Date(couponData.validFrom);
        if (!isNaN(validFrom.getTime()))
          couponData.validFrom = new Date(couponData.validFrom);
      }

      reset(couponData);
    }

    // Redirect to Edit page after creating a new coupon
    if (
      id == "new" &&
      coupon?.id &&
      coupon?.id !== initialId &&
      coupon?._version == 1
    ) {
      history.push(`/coupon/${coupon.id}`);
    }
  }, [coupon?.id]);

  const prepareCouponObject = (data) => {
    const availableDays =
      data.availableDays &&
      data.availableDays
        .filter((row) => row.day && row.opensAt && row.closesAt)
        .map((row) => ({
          day: row.day,
          opensAt: format(row.opensAt, "HH:mm"),
          closesAt: format(row.closesAt, "HH:mm"),
        }));

    data.autoApplyConditions = {
      ...data.autoApplyConditions,
      alwaysApply: !!data.alwaysApply,
    };
    delete data.alwaysApply;

    if (data.minCartValue) {
      data.autoApplyConditions = {
        ...data.autoApplyConditions,
        minCartValue: data.minCartValue,
      };
      delete data.minCartValue;
    }

    data.enabled = !!data.enabled;
    return { ...data, availableDays };
  };

  const onSubmit = async (data) => {
    if (data.validFrom && data.expireDate && data.validFrom > data.expireDate) {
      addNotification(
        {
          title: (
            <React.Fragment>
              <strong>Error</strong>
            </React.Fragment>
          ),
          message: "Valid From date should be less than Expire Date",
          icon: `warning`,
        },
        5000
      );
      return;
    }
    setLoading(true);
    const coupon = prepareCouponObject(data);
    await saveCoupon(cleanDeep(coupon));
    setLoading(false);
  };

  if (
    (!coupon && id !== "new") ||
    (coupon && id !== "new" && id !== coupon.id) ||
    (id === "new" && coupon)
  ) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">Loading...</div>
    );
  }

  return (
    <React.Fragment>
      <PageHeading item={coupon} isNew={id === "new"} label={coupon?.name} />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Coupon Details
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-500">
                This information will be displayed publicly.
              </p>
            </div>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="shadow sm:rounded-md sm:overflow-hidden">
                <div className="px-4 py-5 bg-white sm:p-6">
                  {coupon?._deleted && (
                    <div
                      style={{
                        backgroundColor: "red",
                        color: "white",
                        borderRadius: "4px",
                        padding: "5px 10px",
                        marginBottom: "10px",
                      }}
                    >
                      This coupon has been deleted. Please return to the list to
                      access active coupons.
                    </div>
                  )}

                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        defaultValue={coupon?.name}
                        required={true}
                        ref={register}
                        className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-3 flex items-center">
                      <div className="flex items-center h-5">
                        <input
                          id="enabled"
                          name="enabled"
                          type="checkbox"
                          ref={register}
                          className="form-checkbox h-4 w-4 text-indigo-600"
                        />
                      </div>
                      <div className="pl-2 text-sm leading-5">
                        <label
                          htmlFor="enabled"
                          className="font-medium text-gray-700"
                        >
                          Enabled
                        </label>
                      </div>
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="locationID"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Location
                      </label>
                      <select
                        id="locationID"
                        name="locationID"
                        ref={register}
                        className="mt-1 block form-select w-full py-2 px-3 py-0 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                      >
                        {locations &&
                          locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {`${location.name} ${
                                location.address?.postCode
                                  ? "(" + location.address?.postCode + ")"
                                  : ""
                              }`}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="usageLimit"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Usage Limit
                      </label>
                      <input
                        id="usageLimit"
                        name="usageLimit"
                        type="number"
                        defaultValue={coupon?.usageLimit}
                        ref={register}
                        className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="discountType"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Type
                      </label>
                      <select
                        id="discountType"
                        name="discountType"
                        defaultValue={
                          coupon?.discountType || DISCOUNT_TYPE.FIXED
                        }
                        ref={register}
                        onChange={(selected) => {
                          setValue(`discountType`, selected.target.value);
                        }}
                        className="mt-1 block form-select w-full py-2 px-3 py-0 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                      >
                        <option value={DISCOUNT_TYPE.FIXED}>
                          {DISCOUNT_TYPE.FIXED}
                        </option>
                        <option value={DISCOUNT_TYPE.PERCENTAGE}>
                          {DISCOUNT_TYPE.PERCENTAGE}
                        </option>
                      </select>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="amount"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        {`${
                          discountType === DISCOUNT_TYPE.PERCENTAGE
                            ? "Percentage"
                            : "Amount"
                        }`}
                      </label>
                      <input
                        id="amount"
                        name="amount"
                        type="number"
                        defaultValue={coupon?.amount}
                        required={true}
                        ref={register}
                        className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                      />
                    </div>

                    <div className="col-span-6 ">
                      <label
                        htmlFor="availableDays"
                        className="block text-sm leading-5 font-medium text-gray-700"
                      >
                        Available Days
                      </label>
                      {availableDaysWithBlank.map((row, index) => {
                        const selectedValue =
                          daysOfWeek &&
                          daysOfWeek.filter(
                            (item) => row.day && row.day.includes(item.value)
                          );
                        return (
                          <div
                            key={index}
                            className="grid grid-cols-6 gap-6 mt-2"
                          >
                            <div className="col-span-4">
                              <Select
                                options={daysOfWeek}
                                value={selectedValue}
                                isMulti
                                onChange={(selected) => {
                                  const weekdaysToSave =
                                    selected &&
                                    selected.map((item) => item.value);

                                  const newavailableDays = availableDays
                                    ? [...availableDays]
                                    : [];
                                  if (newavailableDays[index]) {
                                    newavailableDays[index].day =
                                      weekdaysToSave;
                                  } else {
                                    newavailableDays[index] = {
                                      day: weekdaysToSave,
                                    };
                                  }
                                  setValue(`availableDays`, newavailableDays);
                                }}
                              />
                            </div>
                            <div className="col-span-1">
                              <DatePicker
                                selected={row.opensAt}
                                onChange={(selectedTime) => {
                                  const newavailableDays = availableDays
                                    ? [...availableDays]
                                    : [];
                                  if (newavailableDays[index]) {
                                    newavailableDays[index].opensAt =
                                      selectedTime;
                                  } else {
                                    newavailableDays[index] = {
                                      opensAt: selectedTime,
                                    };
                                  }
                                  setValue(`availableDays`, newavailableDays);
                                }}
                                className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                              />
                            </div>
                            <div className="col-span-1">
                              <DatePicker
                                selected={row.closesAt}
                                onChange={(selectedTime) => {
                                  const newavailableDays = availableDays
                                    ? [...availableDays]
                                    : [];
                                  if (newavailableDays[index]) {
                                    newavailableDays[index].closesAt =
                                      selectedTime;
                                  } else {
                                    newavailableDays[index] = {
                                      closesAt: selectedTime,
                                    };
                                  }
                                  setValue(`availableDays`, newavailableDays);
                                }}
                                className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="role"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Valid From
                      </label>
                      <DatePicker
                        selected={validFrom}
                        onChange={(selectedTime) => {
                          setValue(`validFrom`, selectedTime);
                        }}
                        isClearable
                        className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        dateFormat="d MMMM yyyy"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label
                        htmlFor="role"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Expire Date
                      </label>
                      <DatePicker
                        selected={expireDate}
                        onChange={(selectedTime) => {
                          setValue(`expireDate`, selectedTime);
                        }}
                        isClearable
                        className="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        dateFormat="d MMMM yyyy"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-6 flex justify-center pt-5">
                      Auto-apply
                    </div>

                    <div className="col-span-2 sm:col-span-2 flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="alwaysApply"
                          name="alwaysApply"
                          type="checkbox"
                          ref={register}
                          defaultChecked={
                            coupon?.autoApplyConditions?.alwaysApply
                          }
                          className="form-checkbox h-4 w-4 text-indigo-600"
                        />
                      </div>
                      <div className="col-span-2 pl-2 text-sm leading-5">
                        <label
                          htmlFor="alwaysApply"
                          className="block font-medium text-gray-700"
                        >
                          Always
                        </label>
                        <label className="text-sm font-small text-gray-500">
                          Always apply the coupon, regardless of the minimum
                          value
                        </label>
                      </div>
                    </div>

                    <div className="col-span-4 sm:col-span-4">
                      <label
                        htmlFor="minCartValue"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Minimum Transaction Value
                      </label>
                      <label className="text-sm font-small text-gray-500">
                        Auto-apply coupon if transaction meets this minimum
                        value
                      </label>

                      <input
                        id="minCartValue"
                        name="minCartValue"
                        type="number"
                        defaultValue={coupon?.autoApplyConditions?.minCartValue}
                        ref={register}
                        className="mt-1 form-input block py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:col-span-2 sm:text-sm sm:leading-5"
                      />
                    </div>

                    {/* <div className="col-span-6 sm:col-span-6">
                      <label
                        htmlFor="generateMultiple"
                        className="block text-sm font-medium leading-5 text-gray-700"
                      >
                        Generate multiple one time use coupons
                      </label>
                      <label className="text-sm font-small text-gray-500">
                        Specify the number of coupons to generate. Filling in
                        this field will create multiple 'One Time Use' coupons
                        based on this coupon's details. If you do not wish to
                        generate additional coupons, simply leave this field
                        blank and only 1 coupon will be generated, which can be
                        used multiple times, depending on the value set in
                        "usage limit".
                      </label>

                      <input
                        id="generateMultiple"
                        name="generateMultiple"
                        type="number"
                        ref={register}
                        className="mt-1 form-input block py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:col-span-2 sm:text-sm sm:leading-5"
                      />
                    </div> */}

                    <div className="col-span-6 sm:col-span-6">
                      {/* <div className="col-span-6 sm:col-span-6 px-4 py-3">
                        <span className="inline-flex rounded-md shadow-sm">
                          <button
                            disabled={
                              coupon?.enabled === false || coupon?._deleted
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDisplayOneTimeUseCouponModal(true);
                            }}
                            className="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700"
                          >
                            Generate one time use coupons
                          </button>
                        </span>
                      </div> */}
                      {!coupon?._deleted && (
                        <div className="col-span-6 sm:col-span-6 px-4 py-3 text-right sm:px-6">
                          {coupon?.id && (
                            <button
                              type="button"
                              className="mx-2 inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-red-600 hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red active:bg-red-700"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                deleteCoupon(coupon.id);
                              }}
                            >
                              Delete
                            </button>
                          )}
                          <button
                            type="submit"
                            className="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700"
                          >
                            {loading ? "Saving..." : "Save"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
            {/* {coupon?.id && (
              <Modal
                open={displayOneTimeUseCouponModal}
                style={{ position: "fixed" }}
                onClose={() => {
                  setDisplayOneTimeUseCouponModal(false);
                }}
              >
                <div>
                  <div className="mx-auto mt-10 max-w-sm">
                    <div className="bg-gray-50 p-4 shadow">
                      <div className="block text-center font-medium leading-5 text-gray-700 my-1">
                        Insert how many coupons you want to generate
                      </div>

                      <form onSubmit={handleSubmit(generateOneTimeUseCoupons)}>
                        TODO
                      </form>
                    </div>
                  </div>
                </div>
              </Modal>
            )} */}
          </div>
        </div>
      </div>
      {coupon?.id && (
        <Tab>
          <CouponCustomers label="Customers Using Coupon" />
          <CouponBookings label="Bookings Using Coupon" />
          {/* <OneTimeUseCoupon
          oneTimeUseCoupons={coupon?.oneTimeUseCoupons?.items}
          label="One Time Use Coupons"
        /> */}
        </Tab>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    locations: state.location.list,
    coupon: state.coupon.item,
  };
};

const mapDispatchToProps = (dispatch) => ({
  listLocations: (limit, nextToken) =>
    dispatch(LOCATIONACTIONS.getList(limit, nextToken)),
  saveCoupon: (item) => dispatch(ACTIONS.saveItem(item)),
  getCoupon: (item) => dispatch(ACTIONS.getItem(item)),
  clearCoupon: () => dispatch(ACTIONS.clearItem()),
  deleteCoupon: (id) => dispatch(ACTIONS.deleteItem(id)),
  addNotification: (message, hideAfter) =>
    dispatch(NOTIFICATIONACTIONS.addNotification(message, hideAfter)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Coupon);
